import { SpamFill } from "components/icons/icn-spam-fill";
import SuggestionsListHeader from "components/suggestions-list/suggestions-list-header";
import { TextSpan } from "components/text-label";
import moment from "moment";
import { useMemo, useState } from "react";
import intl from "react-intl-universal";
import { formatNumber } from "utils/format";
import { PanelDetails } from "./components/panel";
import {
  ColDetails,
  ContentWrapper,
  Details,
  InfosWrapper,
  ItemsWrapper,
  LastUpdateRow,
  RowDetails,
  StockValueRow,
  TitleRow,
  TitlesWrapper,
  Wrapper,
} from "./styled";

export interface Stock {
  Name: string;
  LastUpdate: string | null;
  Stock: number;
  UnitOfMeasurement: string;
  ExitSuggestions: ExitSuggestions[];
}

export interface SharedExternalWarehouseData {
  Title: string;
  Stocks: Stock[];
  TotalStock: number;
}

export interface ExitSuggestions {
  Date: Date;
  DestinationPlant: string;
  MaterialId: number;
  Quantity: number;
}

interface SharedExternalWarehouseProps {
  data: SharedExternalWarehouseData[];
  show: boolean;
}

interface SharedExternalWarehouseItemProps {
  data: SharedExternalWarehouseData;
  isExpanded: boolean;
  onToggle: () => void;
}

const SharedExternalWarehouseItem = ({
  data,
  isExpanded,
  onToggle,
}: SharedExternalWarehouseItemProps) => {
  const hasExitSuggestions = useMemo(() => {
    if (!data?.Stocks || data.Stocks.length === 0) {
      return false;
    }

    return data.Stocks.some((stock) => {
      if (!stock.ExitSuggestions || stock.ExitSuggestions.length === 0) {
        return false;
      }

      return true;
    });
  }, [data]);

  if (!data?.Stocks || data.Stocks.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <SuggestionsListHeader
        isItem
        hasIcon
        isOrder={true}
        itemsExpanded={isExpanded}
        checked={false}
        expandAllItems={onToggle}
        hideMargin
      >
        <TitlesWrapper>
          <TextSpan
            color={"var(--color-neutral-300)"}
            display={"block"}
            style={{ fontSize: "11px", fontWeight: "var(--font-weight-semibold)" }}
          >
            {data.Title.toUpperCase()}
          </TextSpan>
          <TitleRow>
            <TextSpan
              color={
                hasExitSuggestions
                  ? "var(--color-feedback-warning-300)"
                  : "var(--color-contrast-brand)"
              }
              display={"block"}
              style={{ fontSize: "16px", fontWeight: "var(--font-weight-semibold)" }}
            >
              {formatNumber(data.TotalStock)}
            </TextSpan>
            {hasExitSuggestions ? (
              <SpamFill size={10} color="var(--color-feedback-warning-300)" />
            ) : null}
          </TitleRow>
        </TitlesWrapper>
      </SuggestionsListHeader>
    </Wrapper>
  );
};

interface SharedExternalWarehouseContentProps {
  data: SharedExternalWarehouseData | null;
  isExpanded: boolean;
}

const SharedExternalWarehouseContent = ({
  data,
  isExpanded,
}: SharedExternalWarehouseContentProps) => (
  <ContentWrapper data-testid="shared-external-warehouse-content" itemExpanded={isExpanded}>
    {data ? (
      <Details>
        {data.Stocks.map((stock, index) => {
          const hasExitSuggestion = stock.ExitSuggestions?.length > 0;

          return (
            <div key={`${stock.Name}-${index}`}>
              <RowDetails>
                <ColDetails xs={8}>
                  <InfosWrapper>
                    <TextSpan
                      color={"var(--color-action-default)"}
                      display={"block"}
                      style={{
                        fontSize: "15px",
                        fontWeight: "var(--font-weight-semibold)",
                      }}
                      title={stock.Name}
                    >
                      {stock.Name}
                    </TextSpan>
                    {stock.LastUpdate ? (
                      <LastUpdateRow>
                        <TextSpan
                          color={"var(--color-action-default)"}
                          display={"block"}
                          style={{
                            fontSize: "12px",
                          }}
                        >
                          {intl.get("commons.lastUpdate")}:{" "}
                          {moment(stock.LastUpdate).format("[DM]")}
                          {` ${intl.get("commons.at")} `}
                          {moment(stock.LastUpdate).format("HH:mm")}
                        </TextSpan>
                      </LastUpdateRow>
                    ) : null}
                  </InfosWrapper>
                </ColDetails>
                <ColDetails xs={4}>
                  <InfosWrapper>
                    <TextSpan
                      color={"var(--color-neutral-300)"}
                      display={"block"}
                      style={{
                        fontSize: "11px",
                        fontWeight: "var(--font-weight-semibold)",
                      }}
                    >
                      {intl.get("commons.stock").toUpperCase()}
                    </TextSpan>
                    <StockValueRow>
                      <TextSpan
                        color={
                          hasExitSuggestion
                            ? "var(--color-feedback-warning-300)"
                            : "var(--color-contrast-brand)"
                        }
                        display={"block"}
                        style={{
                          fontSize: "16px",
                          fontWeight: "var(--font-weight-semibold)",
                        }}
                      >
                        {formatNumber(stock.Stock)} {stock.UnitOfMeasurement}
                      </TextSpan>
                      {hasExitSuggestion ? (
                        <SpamFill size={10} color="var(--color-feedback-warning-300)" />
                      ) : null}
                    </StockValueRow>
                  </InfosWrapper>
                </ColDetails>
              </RowDetails>
              {stock.ExitSuggestions?.length > 0 ? (
                <PanelDetails
                  exitSuggestions={stock.ExitSuggestions}
                  unitOfMeasurement={stock.UnitOfMeasurement}
                ></PanelDetails>
              ) : null}
            </div>
          );
        })}
      </Details>
    ) : null}
  </ContentWrapper>
);

const SharedExternalWarehouse = (props: SharedExternalWarehouseProps) => {
  const { data, show } = props;
  const [expandedItemKey, setExpandedItemKey] = useState<string | null>(null);
  const validData = data?.filter((externalWarehouse) => externalWarehouse?.Stocks?.length > 0);
  const expandedItem = validData?.find(
    (externalWarehouse, index) => `${externalWarehouse.Title}-${index}` === expandedItemKey
  );

  const handleToggleItem = (itemKey: string) => {
    setExpandedItemKey((currentItemKey) => (currentItemKey === itemKey ? null : itemKey));
  };

  if (!show || !validData?.length) {
    return null;
  }

  return (
    <>
      <ItemsWrapper data-testid="shared-external-warehouse-items">
        {validData.map((externalWarehouse, index) => {
          const itemKey = `${externalWarehouse.Title}-${index}`;

          return (
            <SharedExternalWarehouseItem
              key={itemKey}
              data={externalWarehouse}
              isExpanded={expandedItemKey === itemKey}
              onToggle={() => handleToggleItem(itemKey)}
            />
          );
        })}
      </ItemsWrapper>

      <SharedExternalWarehouseContent
        data={expandedItem ?? null}
        isExpanded={Boolean(expandedItem)}
      />
    </>
  );
};

export default SharedExternalWarehouse;
